import {
  SECURITY_EFFECTIVE_DATE,
  SECURITY_NAV,
  SECURITY_PAGE_SUBTITLE,
  getSecuritySectionById,
  securitySections,
} from "../app/landing/securityContent";
import { applySecurityGeoSeo } from "./geoSeo";

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function isSecurityPath(): boolean {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  return path === "/security" || path.startsWith("/security/");
}

export function getSecuritySlug(): string {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  if (path === "/security") return "overview";
  const match = path.match(/^\/security\/([^/]+)$/);
  const slug = match?.[1] ?? "overview";
  return getSecuritySectionById(slug) ? slug : "overview";
}

export function securitySectionUrl(slug: string): string {
  return slug === "overview" ? "/security" : `/security/${slug}`;
}

function renderBullets(bullets: string[]): string {
  return `<ul class="toolkit-tips">${bullets.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul>`;
}

function renderParagraphs(paragraphs: string[]): string {
  return paragraphs.map((p) => `<p>${escapeHtml(p)}</p>`).join("");
}

function renderHtmlParagraphs(paragraphs: string[]): string {
  return paragraphs.map((p) => `<p>${p}</p>`).join("");
}

function renderSectionBody(section: (typeof securitySections)[number]): string {
  let html = `<p class="toolkit-section-lead">${escapeHtml(section.lead)}</p>`;
  if (section.paragraphs?.length) {
    html += renderParagraphs(section.paragraphs);
  }
  if (section.htmlParagraphs?.length) {
    html += renderHtmlParagraphs(section.htmlParagraphs);
  }
  if (section.bullets?.length) {
    html += renderBullets(section.bullets);
  }
  if (section.subsections?.length) {
    html += section.subsections
      .map((sub) => {
        let block = `<div class="toolkit-subsection"><h3>${escapeHtml(sub.heading)}</h3>`;
        if (sub.paragraphs?.length) {
          block += renderParagraphs(sub.paragraphs);
        }
        if (sub.bullets?.length) {
          block += renderBullets(sub.bullets);
        }
        block += "</div>";
        return block;
      })
      .join("");
  }
  return html;
}

function renderSecuritySection(section: (typeof securitySections)[number]): string {
  return `<section id="${escapeHtml(section.id)}" class="toolkit-section" data-security-section="${escapeHtml(section.id)}">
    <div class="toolkit-section-header">
      <h2>${escapeHtml(section.title)}</h2>
      <button type="button" class="toolkit-copy-link" data-copy-slug="${escapeHtml(section.id)}" aria-label="Copy link to ${escapeHtml(section.title)}">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
        <span class="toolkit-copy-link-label">Copy link</span>
      </button>
    </div>
    ${renderSectionBody(section)}
  </section>`;
}

function renderSidebarNav(): string {
  return SECURITY_NAV.map(
    (item) =>
      `<a href="${escapeHtml(securitySectionUrl(item.id))}" class="toolkit-sidebar-link" data-security-nav="${escapeHtml(item.id)}">${escapeHtml(item.label)}</a>`,
  ).join("");
}

function getHeaderOffset(): number {
  const header = document.getElementById("marketing-header");
  return header ? header.getBoundingClientRect().height : 72;
}

function scrollToSecuritySlug(slug: string, smooth: boolean): void {
  const target = document.getElementById(slug);
  if (!target) return;
  const top = target.getBoundingClientRect().top + window.scrollY - getHeaderOffset() - 16;
  window.scrollTo({ top: Math.max(0, top), behavior: smooth ? "smooth" : "auto" });
}

function setActiveNav(slug: string): void {
  document.querySelectorAll<HTMLElement>(".toolkit-sidebar-link[data-security-nav]").forEach((link) => {
    const isActive = link.dataset.securityNav === slug;
    link.classList.toggle("is-active", isActive);
    if (isActive) {
      link.setAttribute("aria-current", "location");
    } else {
      link.removeAttribute("aria-current");
    }
  });
}

function navigateToSlug(slug: string): void {
  setActiveNav(slug);
  scrollToSecuritySlug(slug, true);
  const url = securitySectionUrl(slug);
  window.history.pushState({ securitySlug: slug }, "", url);
  applySecurityGeoSeo(slug);
  closeMobileNav();
}

function closeMobileNav(): void {
  document.getElementById("security-sidebar")?.classList.remove("is-open");
  document.getElementById("security-sidebar-backdrop")?.setAttribute("hidden", "");
  document.getElementById("security-sidebar-backdrop")?.setAttribute("aria-hidden", "true");
  const toggle = document.getElementById("security-mobile-nav-toggle");
  toggle?.setAttribute("aria-expanded", "false");
}

function openMobileNav(): void {
  document.getElementById("security-sidebar")?.classList.add("is-open");
  const backdrop = document.getElementById("security-sidebar-backdrop");
  backdrop?.removeAttribute("hidden");
  backdrop?.setAttribute("aria-hidden", "false");
  const toggle = document.getElementById("security-mobile-nav-toggle");
  toggle?.setAttribute("aria-expanded", "true");
}

function setupSecurityNavigation(): void {
  document.querySelectorAll<HTMLAnchorElement>("[data-security-nav]").forEach((link) => {
    link.addEventListener("click", (event) => {
      const slug = link.dataset.securityNav;
      if (!slug) return;
      event.preventDefault();
      navigateToSlug(slug);
    });
  });

  window.addEventListener("popstate", () => {
    const slug = getSecuritySlug();
    applySecurityGeoSeo(slug);
    setActiveNav(slug);
    scrollToSecuritySlug(slug, false);
  });
}

function setupSecurityScrollSpy(): void {
  const sections = [...document.querySelectorAll<HTMLElement>("[data-security-section]")];
  if (!sections.length) return;

  let ticking = false;

  const updateActiveSection = (): void => {
    const offset = getHeaderOffset() + 20;
    let activeSlug = sections[0]?.dataset.securitySection ?? "overview";

    for (const section of sections) {
      if (section.getBoundingClientRect().top <= offset) {
        activeSlug = section.dataset.securitySection ?? activeSlug;
      } else {
        break;
      }
    }

    const current = document.querySelector<HTMLElement>(".toolkit-sidebar-link.is-active[data-security-nav]")
      ?.dataset.securityNav;
    if (current !== activeSlug) {
      setActiveNav(activeSlug);
    }
    ticking = false;
  };

  const scheduleUpdate = (): void => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(updateActiveSection);
  };

  window.addEventListener("scroll", scheduleUpdate, { passive: true });
  window.addEventListener("resize", scheduleUpdate);
  updateActiveSection();
}

function setupCopyLinks(): void {
  document.querySelectorAll<HTMLButtonElement>(".toolkit-copy-link[data-copy-slug]").forEach((button) => {
    if (!button.closest("#static-security-page")) return;
    button.addEventListener("click", () => {
      const slug = button.dataset.copySlug;
      if (!slug) return;
      const url = `${window.location.origin}${securitySectionUrl(slug)}`;
      void navigator.clipboard.writeText(url).then(() => {
        const label = button.querySelector(".toolkit-copy-link-label");
        if (label) {
          const original = label.textContent;
          label.textContent = "Copied!";
          window.setTimeout(() => {
            label.textContent = original;
          }, 1500);
        }
      });
    });
  });
}

function setupMobileNav(): void {
  const toggle = document.getElementById("security-mobile-nav-toggle");
  const backdrop = document.getElementById("security-sidebar-backdrop");
  toggle?.addEventListener("click", () => {
    if (document.getElementById("security-sidebar")?.classList.contains("is-open")) {
      closeMobileNav();
    } else {
      openMobileNav();
    }
  });
  backdrop?.addEventListener("click", closeMobileNav);
  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && document.getElementById("security-sidebar")?.classList.contains("is-open")) {
      closeMobileNav();
    }
  });
}

export function showSecurityPage(): void {
  const marketingPage = document.getElementById("static-marketing-page");
  const pricingPage = document.getElementById("static-pricing-page");
  const toolkitPage = document.getElementById("static-toolkit-page");
  const securityPage = document.getElementById("static-security-page");
  const changelogPage = document.getElementById("static-changelog-page");
  if (marketingPage) marketingPage.hidden = true;
  if (pricingPage) pricingPage.hidden = true;
  if (toolkitPage) toolkitPage.hidden = true;
  if (changelogPage) changelogPage.hidden = true;
  if (securityPage) securityPage.hidden = false;
}

export function setupSecurityPage(wireLegalLinks: (root?: ParentNode) => void): void {
  const subtitle = document.getElementById("security-page-subtitle");
  const meta = document.getElementById("security-page-meta");
  const nav = document.getElementById("security-sidebar-nav");
  const main = document.getElementById("security-main");
  if (!subtitle || !meta || !nav || !main) return;

  subtitle.textContent = SECURITY_PAGE_SUBTITLE;
  meta.textContent = `${SECURITY_EFFECTIVE_DATE} · ${securitySections.length} sections`;
  nav.innerHTML = renderSidebarNav();
  main.innerHTML = securitySections.map(renderSecuritySection).join("");

  const slug = getSecuritySlug();
  applySecurityGeoSeo(slug);
  setupSecurityNavigation();
  setupSecurityScrollSpy();
  setupCopyLinks();
  setupMobileNav();
  wireLegalLinks(main);

  requestAnimationFrame(() => {
    setActiveNav(slug);
    scrollToSecuritySlug(slug, false);
    if (slug !== "overview") {
      window.history.replaceState({ securitySlug: slug }, "", securitySectionUrl(slug));
    }
  });
}
